/**
 * Berechnung der Tooltip-Position in Dokumentkoordinaten.
 *
 * Bewusst eine reine Funktion ohne DOM-Zugriff: Die Messung (Rechtecke, Scroll,
 * Viewport) passiert in der Komponente, das Rechnen hier. Nur so ist die
 * Positionierung — inklusive der Begrenzung auf den sichtbaren Bereich —
 * ueberhaupt testbar; jsdom hat keine Layout-Engine und liefert sonst nur Nullen.
 */

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TriggerRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

export interface TooltipSize {
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
}

/** Mindestabstand zum Rand des sichtbaren Bereichs. */
const EDGE_MARGIN = 4;

export function calculateTooltipPosition(
  trigger: TriggerRect,
  tooltip: TooltipSize,
  placement: TooltipPlacement,
  offset: number,
  viewport: Viewport
): { top: number; left: number } {
  const { scrollX, scrollY } = viewport;
  let top = 0;
  let left = 0;

  switch (placement) {
    case 'top':
      top = trigger.top + scrollY - tooltip.height - offset;
      left = trigger.left + scrollX + (trigger.width - tooltip.width) / 2;
      break;
    case 'bottom':
      top = trigger.bottom + scrollY + offset;
      left = trigger.left + scrollX + (trigger.width - tooltip.width) / 2;
      break;
    case 'left':
      top = trigger.top + scrollY + (trigger.height - tooltip.height) / 2;
      left = trigger.left + scrollX - tooltip.width - offset;
      break;
    case 'right':
      top = trigger.top + scrollY + (trigger.height - tooltip.height) / 2;
      left = trigger.right + scrollX + offset;
      break;
  }

  // Begrenzung auf den sichtbaren Bereich.
  // Hinweis: Die untere Grenze rechnet den vertikalen Scroll ein, die rechte
  // Grenze den horizontalen NICHT — unveraendert aus der urspruenglichen
  // Implementierung uebernommen (siehe Test "dokumentiertes Ist-Verhalten").
  if (left < EDGE_MARGIN) left = EDGE_MARGIN;
  if (left + tooltip.width > viewport.width - EDGE_MARGIN) {
    left = viewport.width - tooltip.width - EDGE_MARGIN;
  }
  if (top < EDGE_MARGIN) top = EDGE_MARGIN;
  if (top + tooltip.height > viewport.height + scrollY - EDGE_MARGIN) {
    top = viewport.height + scrollY - tooltip.height - EDGE_MARGIN;
  }

  return { top, left };
}
